/**
 * TODO: MAKE THIS INTO AN IndividualsContext or something so it can fetch information about any role
 */
/* eslint-disable @typescript-eslint/camelcase */
import React, { createContext, useCallback, useContext, useState } from "react";

import { errorToast } from "src/shared/components/Toast";
import { EVENT_ID } from "src/shared/constants/event";

import { useCreateVolunteerShiftMutation } from "../../../shifts/graphql/createVolunteerShift.generated";
import { TPrimaryRoles } from "../../constants/roles";
import { BaseShiftState, convertDayToDate } from "../constants";

interface AddShiftState extends BaseShiftState {
  createShift: () => Promise<void>;
}

const DEFAULT_STATE: AddShiftState = {
  shiftName: "",
  shiftDateStart: "",
  shiftDateEnd: "",
  timeStart: "",
  timeEnd: "",
  location: "",
  shiftLead: "",
  shiftLeadId: null,
  roles: "",
  supplementaryType: "",
  requiredVolunteers: 0,
  minVolunteers: 0,
  description: "",

  updateShiftName: () => Promise.reject(),
  updateShiftDateStart: () => Promise.reject(),
  updateShiftDateEnd: () => Promise.reject(),
  updateTimeStart: () => Promise.reject(),
  updateTimeEnd: () => Promise.reject(),
  updateLocation: () => Promise.reject(),
  updateShiftLead: () => Promise.reject(),
  updateRoles: () => Promise.reject(),
  updateSupplementaryType: () => Promise.reject(),
  updateRequiredVolunteers: () => Promise.reject(),
  updateMinVolunteers: () => Promise.reject(),
  updateDescription: () => Promise.reject(),

  createShift: () => Promise.reject(),
};

const AddShiftContext: React.Context<AddShiftState> =
  createContext(DEFAULT_STATE);

export const useAddShiftContext = () => useContext(AddShiftContext);

export const AddShiftContextProvider: React.FC = ({ children }) => {
  const [data, setData] = useState<AddShiftState>(() => DEFAULT_STATE);
  const [createShiftMutation] = useCreateVolunteerShiftMutation();

  const updateShiftName = useCallback((name: string) => {
    setData((prevState) => ({ ...prevState, shiftName: name }));
  }, []);

  const updateShiftDateStart = useCallback((date: string) => {
    setData((prevState) => ({ ...prevState, shiftDateStart: date }));
  }, []);

  const updateShiftDateEnd = useCallback((date: string) => {
    console.log(date);
    setData((prevState) => ({ ...prevState, shiftDateEnd: date }));
  }, []);

  const updateTimeStart = useCallback((time: string) => {
    setData((prevState) => ({ ...prevState, timeStart: time }));
  }, []);

  const updateTimeEnd = useCallback((time: string) => {
    setData((prevState) => ({ ...prevState, timeEnd: time }));
  }, []);

  const updateLocation = useCallback((location: string) => {
    setData((prevState) => ({ ...prevState, location }));
  }, []);

  const updateShiftLead = useCallback((lead: string, leadId: number | null) => {
    setData((prevState) => ({
      ...prevState,
      shiftLead: lead,
      shiftLeadId: leadId,
    }));
  }, []);

  const updateRoles = useCallback((role: string) => {
    setData((prevState) => ({ ...prevState, roles: role }));
  }, []);

  const updateSupplementaryType = useCallback((type: string) => {
    setData((prevState) => ({ ...prevState, supplementaryType: type }));
  }, []);

  const updateRequiredVolunteers = useCallback((num: number) => {
    setData((prevState) => ({ ...prevState, requiredVolunteers: num }));
  }, []);

  const updateMinVolunteers = useCallback((num: number) => {
    setData((prevState) => ({ ...prevState, minVolunteers: num }));
  }, []);

  const updateDescription = useCallback((description: string) => {
    setData((prevState) => ({ ...prevState, description }));
  }, []);

  const createShift = useCallback(async (): Promise<void> => {
    // The id is captured when the lead is picked, so it always identifies the
    // exact account that was selected as names are not unique.
    if (data.shiftLeadId === null) {
      errorToast("Please select a shift lead from the dropdown.");
      throw new Error("Shift lead not selected");
    }

    try {
      const dayStart = convertDayToDate(data.shiftDateStart);
      const dayEnd = convertDayToDate(data.shiftDateEnd);

      await createShiftMutation({
        variables: {
          category_name:
            data.roles === TPrimaryRoles.SUPPLEMENTARY
              ? data.supplementaryType
              : data.roles,
          organizer_responsible_user_id: data.shiftLeadId,
          preferred_min_signups: data.minVolunteers,
          start_time: new Date(`${dayStart}T${data.timeStart}:00`),
          end_time: new Date(`${dayEnd}T${data.timeEnd}:00`),
          location: data.location,
          event_id: EVENT_ID,
          name: data.shiftName,
          max_count: data.requiredVolunteers,
          description: data.description,
        },
      });
    } catch (e) {
      console.error(e);
      errorToast("We couldn't create that shift. Please try again.");
      throw e;
    }
  }, [createShiftMutation, data]);

  const state = {
    ...data,
    updateShiftName,
    updateShiftDateStart,
    updateShiftDateEnd,
    updateTimeStart,
    updateTimeEnd,
    updateLocation,
    updateShiftLead,
    updateRoles,
    updateSupplementaryType,
    updateRequiredVolunteers,
    updateMinVolunteers,
    updateDescription,
    createShift,
  };

  return (
    <AddShiftContext.Provider value={state}>
      {children}
    </AddShiftContext.Provider>
  );
};
